import React, { useRef, useState } from "react";
import type { CarouselRef } from "antd/es/carousel";
import { Modal, Carousel } from "antd";
import VideoPlayer from "../../../Components/VideoPlayer";
import VisorImagen from "../../../Components/VisorImagen";

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/**
 * Un elemento del carrusel: la URL a secas (compat) o la URL con la versión liviana del video
 * (`previewUrl` del entry de storage), para que el video abra en 480p y se pueda pasar a HD.
 *
 * `kind` es el tipo que trae el entry de storage ("image" | "video" | "doc"). Las URLs firmadas
 * `/files/<token>` no tienen extensión, así que sin `kind` un video del servidor no se
 * distingue de una foto: la regex sobre la extensión solo cubre las URLs legacy.
 */
export type ImagenCarrusel =
  | string
  | { url: string; previewUrl?: string | null; kind?: string };

const esVideo = (url: string, kind?: string | null): boolean =>
  kind === "video" || VIDEO_RE.test(url);

interface ImageCarouselModalProps {
  open: boolean;
  images: ImagenCarrusel[];
  onClose: () => void;
}

const ImageCarouselModal: React.FC<ImageCarouselModalProps> = ({
  open,
  images,
  onClose,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carrusel = useRef<CarouselRef>(null);
  const items = images.map((it) =>
    typeof it === "string"
      ? { url: it, previewUrl: null as string | null, kind: undefined as string | undefined }
      : { url: it.url, previewUrl: it.previewUrl ?? null, kind: it.kind },
  );

  return (
    <Modal
      open={open}
      title="Imágenes"
      onCancel={onClose}
      footer={null}
      width={960}
      styles={{
        body: { paddingBottom: 56, maxHeight: "80vh", overflow: "hidden" },
      }}
    >
      {images.length > 0 ? (
        <Carousel
          ref={carrusel}
          arrows
          dots
          infinite={images.length > 1}
          afterChange={(idx) => setCurrentSlide(idx)}
        >
          {items.map(({ url, previewUrl, kind }, i) => (
            <div key={`${url}-${i}`}>
              <div
                style={{
                  position: "relative",
                  height: "60vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#111",
                }}
              >
                {esVideo(url, kind) ? (
                  <VideoPlayer
                    src={url}
                    previewSrc={previewUrl}
                    ajuste="llenar"
                  />
                ) : (
                  <VisorImagen src={url} alt={`Imagen ${i + 1}`} altura="calc(60vh - 52px)" onSwipe={(paso) => paso > 0 ? carrusel.current?.next() : carrusel.current?.prev()} />
                )}

                <div
                  style={{
                    position: "absolute",
                    left: 12,
                    top: 12,
                    zIndex: 9,
                    background: "rgba(0,0,0,.55)",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                >
                  {currentSlide + 1} / {images.length}
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      ) : (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
          No hay imagenes para esta propiedad.
        </div>
      )}
    </Modal>
  );
};

export default ImageCarouselModal;
